// Resuelve la numeración multinivel "real" de un .docx (la que Word calcula
// a partir de word/numbering.xml) en el orden en que aparecen los párrafos
// numerados en el documento.
//
// Por qué existe: mammoth (la librería que usamos para convertir Word a HTML,
// ver wordToHtml.ts) solo expone `paragraph.numbering.level` por párrafo, sin
// el numId, el valor de arranque (`w:start`) ni el formato compuesto de
// niveles (`w:lvlText`, p. ej. "%1.%2."). Eso hace que listas cuyo primer
// nivel arranca en un número distinto de 1 (p. ej. un sub-punto "7.1", "7.2"
// que en el XML es una lista independiente con start=7) se muestren como
// "1.", "2." en vez de "7.1.", "7.2.". Este módulo relee el .docx crudo y
// calcula la etiqueta completa de cada párrafo numerado, tal como la
// mostraría Word.
import JSZip from 'jszip'

interface LevelDef {
  start: number
  numFmt: string
  lvlText: string
}

function parseXml(text: string): Document {
  return new DOMParser().parseFromString(text, 'application/xml')
}

function directChildren(el: Element, tag: string): Element[] {
  return Array.from(el.children).filter(c => c.tagName === tag)
}

function attrVal(el: Element | null): number | null {
  if (!el) return null
  const v = el.getAttribute('w:val')
  return v === null ? null : Number(v)
}

function attrStr(el: Element | null): string | null {
  if (!el) return null
  return el.getAttribute('w:val')
}

function romanUpper(n: number): string {
  const vals: [number, string][] = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ]
  let out = ''
  for (const [v, s] of vals) {
    while (n >= v) { out += s; n -= v }
  }
  return out
}

function lettersLower(n: number): string {
  let out = ''
  while (n > 0) {
    const r = (n - 1) % 26
    out = String.fromCharCode(97 + r) + out
    n = Math.floor((n - 1) / 26)
  }
  return out
}

function formatCounter(value: number, numFmt: string): string {
  switch (numFmt) {
    case 'decimal': return String(value)
    case 'decimalZero': return String(value).padStart(2, '0')
    case 'lowerLetter': return lettersLower(value)
    case 'upperLetter': return lettersLower(value).toUpperCase()
    case 'lowerRoman': return romanUpper(value).toLowerCase()
    case 'upperRoman': return romanUpper(value)
    default: return String(value)
  }
}

class NumberingResolver {
  private abstractLevels = new Map<string, Map<number, LevelDef>>()
  private numToAbstract = new Map<string, string>()
  private numOverrides = new Map<string, Map<number, Partial<LevelDef>>>()
  private styleNumPr = new Map<string, { numId: string; ilvl: number | null }>()
  private counters = new Map<string, Map<number, number>>()

  constructor(numberingDoc: Document | null, stylesDoc: Document | null) {
    if (numberingDoc) this.parseNumbering(numberingDoc)
    if (stylesDoc) this.parseStyles(stylesDoc)
  }

  private parseNumbering(doc: Document) {
    const root = doc.documentElement
    for (const abstractNum of Array.from(root.getElementsByTagName('w:abstractNum'))) {
      const id = abstractNum.getAttribute('w:abstractNumId')
      if (id === null) continue
      const levels = new Map<number, LevelDef>()
      for (const lvl of directChildren(abstractNum, 'w:lvl')) {
        const ilvl = Number(lvl.getAttribute('w:ilvl'))
        const start = attrVal(directChildren(lvl, 'w:start')[0] ?? null) ?? 1
        const numFmt = attrStr(directChildren(lvl, 'w:numFmt')[0] ?? null) ?? 'decimal'
        const lvlText = attrStr(directChildren(lvl, 'w:lvlText')[0] ?? null) ?? `%${ilvl + 1}.`
        levels.set(ilvl, { start, numFmt, lvlText })
      }
      this.abstractLevels.set(id, levels)
    }

    for (const num of Array.from(root.getElementsByTagName('w:num'))) {
      const numId = num.getAttribute('w:numId')
      if (numId === null) continue
      const abstractIdEl = directChildren(num, 'w:abstractNumId')[0] ?? null
      const abstractId = attrStr(abstractIdEl)
      if (abstractId !== null) this.numToAbstract.set(numId, abstractId)

      const overrides = new Map<number, Partial<LevelDef>>()
      for (const lo of directChildren(num, 'w:lvlOverride')) {
        const ilvl = Number(lo.getAttribute('w:ilvl'))
        const entry: Partial<LevelDef> = {}
        const startOverride = directChildren(lo, 'w:startOverride')[0] ?? null
        if (startOverride) entry.start = attrVal(startOverride) ?? undefined
        const lvlEl = directChildren(lo, 'w:lvl')[0] ?? null
        if (lvlEl) {
          const numFmt = attrStr(directChildren(lvlEl, 'w:numFmt')[0] ?? null)
          const lvlText = attrStr(directChildren(lvlEl, 'w:lvlText')[0] ?? null)
          const start = attrVal(directChildren(lvlEl, 'w:start')[0] ?? null)
          if (numFmt !== null) entry.numFmt = numFmt
          if (lvlText !== null) entry.lvlText = lvlText
          if (start !== null) entry.start = start
        }
        if (Object.keys(entry).length > 0) overrides.set(ilvl, entry)
      }
      if (overrides.size > 0) this.numOverrides.set(numId, overrides)
    }
  }

  private parseStyles(doc: Document) {
    const root = doc.documentElement
    for (const style of Array.from(root.getElementsByTagName('w:style'))) {
      const styleId = style.getAttribute('w:styleId')
      if (!styleId) continue
      const pPr = directChildren(style, 'w:pPr')[0] ?? null
      if (!pPr) continue
      const numPr = directChildren(pPr, 'w:numPr')[0] ?? null
      if (!numPr) continue
      const numId = attrStr(directChildren(numPr, 'w:numId')[0] ?? null)
      const ilvl = attrVal(directChildren(numPr, 'w:ilvl')[0] ?? null)
      if (numId !== null) this.styleNumPr.set(styleId, { numId, ilvl })
    }
  }

  /** Definición de nivel para un numId, combinando abstractNum + overrides. Null si no existe. */
  private levelDef(numId: string, ilvl: number): LevelDef | null {
    const abstractId = this.numToAbstract.get(numId)
    const base = abstractId ? this.abstractLevels.get(abstractId)?.get(ilvl) ?? null : null
    const override = this.numOverrides.get(numId)?.get(ilvl)
    if (!base && !override) return null
    return { start: 1, numFmt: 'decimal', lvlText: `%${ilvl + 1}.`, ...base, ...override }
  }

  /** Resuelve (numId, ilvl) para un <w:p>, usando numPr directo o heredado del estilo. */
  resolveNumPr(p: Element): { numId: string; ilvl: number } | null {
    const pPr = directChildren(p, 'w:pPr')[0] ?? null
    if (!pPr) return null

    let numId: string | null = null
    let ilvl = 0
    const numPr = directChildren(pPr, 'w:numPr')[0] ?? null
    if (numPr) {
      numId = attrStr(directChildren(numPr, 'w:numId')[0] ?? null)
      const ilvlVal = attrVal(directChildren(numPr, 'w:ilvl')[0] ?? null)
      if (ilvlVal !== null) ilvl = ilvlVal
    }

    if (numId === null) {
      const pStyle = directChildren(pPr, 'w:pStyle')[0] ?? null
      const styleId = pStyle ? attrStr(pStyle) : null
      const styleEntry = styleId ? this.styleNumPr.get(styleId) : undefined
      if (styleEntry) {
        numId = styleEntry.numId
        if (styleEntry.ilvl !== null) ilvl = styleEntry.ilvl
      }
    }

    if (numId === null || numId === '0') return null
    if (!this.levelDef(numId, ilvl)) return null
    return { numId, ilvl }
  }

  /** Etiqueta visible (p. ej. "7.1.") para la siguiente ocurrencia de (numId, ilvl). */
  render(numId: string, ilvl: number): string | null {
    const def = this.levelDef(numId, ilvl)
    if (!def) return null

    let counters = this.counters.get(numId)
    if (!counters) {
      counters = new Map()
      this.counters.set(numId, counters)
    }

    if (!counters.has(ilvl)) {
      counters.set(ilvl, def.start)
    } else {
      counters.set(ilvl, (counters.get(ilvl) ?? def.start) + 1)
    }

    for (const lvl of Array.from(counters.keys())) {
      if (lvl > ilvl) counters.delete(lvl)
    }

    for (let lvl = 0; lvl < ilvl; lvl++) {
      if (!counters.has(lvl)) {
        counters.set(lvl, this.levelDef(numId, lvl)?.start ?? 1)
      }
    }

    if (def.numFmt === 'bullet') return def.lvlText

    return def.lvlText.replace(/%(\d)/g, (_match, n) => {
      const srcIlvl = Number(n) - 1
      const srcDef = this.levelDef(numId, srcIlvl)
      const value = counters!.get(srcIlvl) ?? srcDef?.start ?? 1
      return formatCounter(value, srcDef?.numFmt ?? 'decimal')
    })
  }
}

/**
 * Devuelve, en orden de aparición, la etiqueta de numeración real (todos los
 * niveles resueltos, p. ej. "7.1.") de cada párrafo del .docx que tiene
 * numeración. Los párrafos sin numeración no generan entrada.
 */
export async function extraerEtiquetasNumeracion(arrayBuffer: ArrayBuffer): Promise<string[]> {
  const zip = await JSZip.loadAsync(arrayBuffer)

  const documentXml = await zip.file('word/document.xml')?.async('text')
  if (!documentXml) return []

  const numberingXml = await zip.file('word/numbering.xml')?.async('text')
  const stylesXml = await zip.file('word/styles.xml')?.async('text')

  const resolver = new NumberingResolver(
    numberingXml ? parseXml(numberingXml) : null,
    stylesXml ? parseXml(stylesXml) : null
  )

  const documentDoc = parseXml(documentXml)
  const paragraphs = Array.from(documentDoc.getElementsByTagName('w:p'))

  const labels: string[] = []
  for (const p of paragraphs) {
    const numPr = resolver.resolveNumPr(p)
    if (!numPr) continue
    const label = resolver.render(numPr.numId, numPr.ilvl)
    if (label !== null) labels.push(label)
  }
  return labels
}
